import React, { useMemo } from "react";
import { DataMapPatchMutation } from "../../../../common/hooks/api/useDataMap";
import { useCollectionDetails } from "../../../../common/hooks/dataMapHooks";
import { useDataMapPendingReceivedPicIds } from "../../../../common/service/resources/data-map/dataMapPending";
import { useDataMapReceivedPIC } from "../../../../common/service/resources/data-map/dataMapTransformers";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import { DataMapDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { DataMapReceivedUpdateOperation } from "../../../../common/service/server/dto/datamap/DataMapRequestDto";
import { OrganizationDataSourceDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UUIDString } from "../../../../common/service/server/Types";
import { DescriptionCheckbox } from "../../../components/input/DescriptionCheckbox";

type DataRecipientSourceCheckboxesProps = {
  group: CollectionGroupDetailsDto;
  source: OrganizationDataSourceDto;
  dataMap: DataMapDto;
  patchDataMap: DataMapPatchMutation;
  includeSelectAll: boolean;
};

export const DataRecipientSourceCheckboxes: React.FC<DataRecipientSourceCheckboxesProps> = ({
  dataMap,
  group: collectionGroup,
  source: dataRecipient,
  patchDataMap,
  includeSelectAll = false,
}) => {
  const { collectedPic } = useCollectionDetails(dataMap, collectionGroup);
  const selected = useDataMapReceivedPIC(dataMap, {
    collectionGroupId: collectionGroup.id,
    vendorId: dataRecipient.vendorId,
  });
  const selectedIds = useMemo(() => selected.map((s) => s.id), [selected]);
  const pendingIds = useDataMapPendingReceivedPicIds(dataMap, {
    collectionGroupId: collectionGroup.id,
    vendorId: dataRecipient.vendorId,
  });

  const handleChange = (picId: UUIDString, checked: boolean) => {
    patchDataMap.mutate({
      operations: [
        {
          target: "RECEIVED",
          action: checked ? "ADD" : "REMOVE",
          groupId: collectionGroup.id,
          picId: picId,
          vendorId: dataRecipient.vendorId,
        },
      ],
    });
  };

  const allSelected = useMemo(
    () => collectedPic.every((pic) => selectedIds.includes(pic.id)),
    [selectedIds, collectedPic],
  );

  const handleSelectAll = async () => {
    // Toggle all the remote selected state
    const collectedIds = collectedPic.map((pic) => pic.id);
    const unselectedIds = collectedIds.filter((id) => !selectedIds.includes(id));

    const operations: DataMapReceivedUpdateOperation[] =
      unselectedIds.length > 0
        ? unselectedIds.map((id) => ({
            target: "RECEIVED",
            action: "ADD",
            groupId: collectionGroup.id,
            picId: id,
            vendorId: dataRecipient.vendorId,
          }))
        : selectedIds.map((id) => ({
            target: "RECEIVED",
            action: "REMOVE",
            groupId: collectionGroup.id,
            picId: id,
            vendorId: dataRecipient.vendorId,
          }));

    patchDataMap.mutate({ operations });
  };

  return (
    <>
      {includeSelectAll && (
        <DescriptionCheckbox
          className="mb-md p-sm"
          label={"Select All"}
          checked={allSelected}
          onChange={handleSelectAll}
        />
      )}

      {collectedPic.map((pic) => {
        return (
          <DescriptionCheckbox
            className="my-sm p-sm"
            key={pic.id}
            label={pic.name}
            checked={selectedIds.includes(pic.id)}
            loading={pendingIds.includes(pic.id)}
            onChange={(_, checked) => handleChange(pic.id, checked)}
          />
        );
      })}
    </>
  );
};
